import {LogLevel} from './constants';
import {Transport, LogLevelHandler, CreateLoggerOptions, Logger} from './types';
import createConsoleTransport from './create-console-transport';
const packageConfig = require('../package.json');

const DEFAULT_NAME = 'log_info_level';
const DEFAULT_BUNDLER_TYPE = 'webpack';

/**
 * Creates a logger instance.
 *
 * @param options
 * @param options.transports The transports to use for the logger
 * @param options.transactionID The transaction ID to send for logs
 * @param options.defaultLevel The log level used by `logger.log`
 * @param options.defaultName The logger name to send for logs
 * @param options.bundlerType The bundler type
 * @param options.applicationName The name of the app using this logger
 * @param options.libraryName The name of the library using this logger
 * @param options.sharedData Static data sent for each log
 * @returns The logger object
 */
export default function createLogger(options: CreateLoggerOptions): Logger {
  const {
    transactionID,
    transports,
    defaultLevel = LogLevel.INFO,
    defaultName = DEFAULT_NAME,
    bundlerType = DEFAULT_BUNDLER_TYPE,
    applicationName = null,
    libraryName = null,
    sharedData = {},
  } = options;

  function createLevelHandler(level: string): LogLevelHandler {
    return function levelHandler({message, data = {}, name = defaultName}) {
      for (const transport of transports) {
        transport({
          loggerName: name,
          level,
          message,
          transactionID,
          data: {
            frameworkLogger: `${packageConfig.name}@${packageConfig.version}`,
            bundlerType,
            ...(applicationName ? {applicationName} : {}),
            ...(libraryName ? {libraryName} : {}),
            ...sharedData,
            ...(data['error']
              ? {
                  stackTrace:
                    data['error'] instanceof Error
                      ? data['error'].stack
                      : new Error().stack,
                }
              : {}),
            ...data,
          },
        });
      }
    };
  }

  return {
    log: createLevelHandler(defaultLevel),
    debug: createLevelHandler(LogLevel.DEBUG),
    info: createLevelHandler(LogLevel.INFO),
    warn: createLevelHandler(LogLevel.WARN),
    error: createLevelHandler(LogLevel.ERROR),
    fatal: createLevelHandler(LogLevel.FATAL),
    set({sharedData = {}, ...overrides}) {
      return createLogger({
        ...options,
        ...overrides,
        sharedData: {
          ...options.sharedData,
          ...sharedData,
        },
      });
    },
  };
}
